import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "./customBaseQuery";

export type LegalDocumentType = "terms-of-service" | "privacy-policy";

export interface LegalDocument {
  platform: string;
  docType: string;
  version: number;
  title: string;
  content: string;
  publishedAt: string;
}

export interface AcceptLegalTermsRequest {
  docType: string;
  version: number;
}

export interface AcceptLegalTermsResponse {
  success: boolean;
  message?: string;
}

export const legalDocumentsApi = createApi({
  reducerPath: "legalDocumentsApi",
  baseQuery: customBaseQuery as any,
  tagTypes: ["LegalDocument"],
  endpoints: (builder) => ({
    getLegalDocument: builder.query<LegalDocument, LegalDocumentType>({
      query: (docType) => ({
        url: `/TermsAndConditions/version/${docType}`,
      }),
      providesTags: (_result, _error, docType) => [
        { type: "LegalDocument", id: docType },
      ],
    }),
    getAllLegalDocuments: builder.query<
      Record<LegalDocumentType, LegalDocument>,
      void
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const documentTypes: LegalDocumentType[] = [
          "terms-of-service",
          "privacy-policy",
        ];

        const results: Record<string, LegalDocument> = {};

        // Fetch all documents in parallel
        const promises = documentTypes.map(async (docType) => {
          const result = await fetchWithBQ({
            url: `/TermsAndConditions/version/${docType}`,
          });

          if (result.error) {
            throw new Error(`Failed to fetch ${docType}: ${result.error}`);
          }

          results[docType] = result.data as LegalDocument;
        });

        try {
          await Promise.all(promises);
          return { data: results as Record<LegalDocumentType, LegalDocument> };
        } catch (error: any) {
          return { error: { status: 500, data: error.message } };
        }
      },
      providesTags: [
        { type: "LegalDocument", id: "terms-of-service" },
        { type: "LegalDocument", id: "privacy-policy" },
      ],
    }),
    acceptLegalTerms: builder.mutation<
      AcceptLegalTermsResponse,
      AcceptLegalTermsRequest
    >({
      query: ({ docType, version }) => ({
        url: "/TermsAndConditions",
        method: "POST",
        body: { docType, version },
      }),
    }),
    acceptAllLegalTerms: builder.mutation<
      AcceptLegalTermsResponse[],
      Record<LegalDocumentType, number>
    >({
      async queryFn(documentVersions, _queryApi, _extraOptions, fetchWithBQ) {
        const results: AcceptLegalTermsResponse[] = [];

        // Accept all documents in parallel
        const promises = Object.entries(documentVersions).map(
          async ([docType, version]) => {
            const result = await fetchWithBQ({
              url: "/TermsAndConditions",
              method: "POST",
              body: { docType, version },
            });

            if (result.error) {
              throw new Error(`Failed to accept ${docType}: ${result.error}`);
            }

            results.push(result.data as AcceptLegalTermsResponse);
          },
        );

        try {
          await Promise.all(promises);
          return { data: results };
        } catch (error: any) {
          return { error: { status: 500, data: error.message } };
        }
      },
    }),
  }),
});

export const {
  useGetLegalDocumentQuery,
  useGetAllLegalDocumentsQuery,
  useLazyGetLegalDocumentQuery,
  useLazyGetAllLegalDocumentsQuery,
  useAcceptLegalTermsMutation,
  useAcceptAllLegalTermsMutation,
} = legalDocumentsApi;
